<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";
import { equipmentGet, equipmentPatch } from "@/common/api/rest/equipment";

import type { EquipmentType } from "@/common/types/equipment";

import { RegisterTheme } from "@/common/themes";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseTable,
  iconMenu,
  svgIcon,
} from "@/common/components";
import {
  equipmentCertificatesForm,
  equipmentDetailsForm,
  equipmentForm,
} from "@/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const accountId = computed(() => String(route.params.id));
const equipmentId = computed(() => String(route.params.equipment));

const equipmentData = ref<EquipmentType | null>(null);

const selectedMenu = ref<string>("home");

const showDelAvatar = ref(false);

const menuTabs = computed(() => [
  {
    key: "home",
    icon: "home-line",
    label: t("common.labels.equipment"),
  },
  {
    key: "details",
    icon: "document-a",
    label: t("common.labels.details"),
  },
  {
    key: "certificate",
    icon: "certificate",
    label: t("common.labels.certificates"),
  },
]);

function back() {
  router.push(`/${accountId.value}/register/equipment`);
}

function fetchData() {
  if (!equipmentId.value) return;

  equipmentGet(accountId.value, equipmentId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        equipmentData.value = { ...data };
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateData(data: Partial<EquipmentType>) {
  equipmentPatch(accountId.value, equipmentId.value, data)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAvatar(avatar: string) {
  fileAvatarCreate(accountId.value, {
    owner_id: equipmentId.value,
    type: "EQUIPMENT",
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(avatar: string) {
  if (!equipmentData.value.avatar) return;

  fileAvatarUpdate(accountId.value, equipmentData.value.avatar.avatar_id, {
    file: avatar,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (equipmentData.value?.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarConfirm() {
  if (!equipmentData.value.avatar) return;

  fileAvatarDelete(accountId.value, equipmentData.value.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        showDelAvatar.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeMenu(key: string) {
  selectedMenu.value = key;
  fetchData();
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <register-theme tab-active="equipment">
    <template #title>
      <div class="flex flex-row justify-between items-center w-full">
        <div class="flex flex-row gap-1 items-center">
          <svg-icon icon="equipment" size="2rem" />
          {{
            t("common.labels.edit", { concat: t("common.labels.equipment") })
          }}
        </div>

        <button type="button" class="btn btn-back btn-sm" @click="back()">
          <svg-icon icon="close" size="1.25rem" />
        </button>
      </div>
    </template>

    <template #content>
      <icon-menu :tabs="menuTabs" :active="selectedMenu" @selected="changeMenu">
        <template #content>
          <div class="forms">
            <template v-if="selectedMenu === 'home'">
              <div class="title">
                {{ t("common.labels.equipment") }}
              </div>

              <base-avatar
                :url="equipmentData?.avatar?.large"
                :is-edit="true"
                @change="changeAvatar"
                @del="delAvatar()"
              />
              <equipment-form :data="equipmentData" @update-data="updateData" />
            </template>

            <template v-if="selectedMenu === 'details'">
              <div class="title">
                {{ t("common.labels.details") }}
              </div>

              <equipment-details-form
                :data="equipmentData"
                @update-data="updateData"
              />
            </template>

            <template v-if="selectedMenu === 'certificate'">
              <div class="title">
                {{ t("common.labels.certificates") }}
              </div>

              <equipment-certificates-form
                :account-id="accountId"
                :equipment-id="equipmentId"
                @update-data="updateData"
              />
            </template>
          </div>
        </template>
      </icon-menu>
    </template>
  </register-theme>

  <base-confirm
    :show="showDelAvatar"
    :title="t('common.labels.remove', { concat: t('common.labels.avatar') })"
    :message="
      t('common.texts.remove_message', { concat: t('common.labels.avatar') })
    "
    @cancel="showDelAvatar = false"
    @confirm="delAvatarConfirm()"
  />
</template>

<style lang="scss" scoped>
.forms {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;

  .title {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;
    border-bottom: 1px solid var(--border);
    margin-block: 2rem;
    padding-block-end: 0.5rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
  }
}
</style>
